import {
  NotificationCloseService,
  toast
} from '../../service/NotificationService';
import { PROP_MAP, type ToastProps } from './interface';
import { useCallback, useEffect, useState } from 'react';

import { NotificationButton } from '../jdsnotificationbutton/generated/index';

import { getPlatform } from '../../utils';
import { modifyProps } from '../../generators/modifyProps';
import { subscribe } from '../../service/pubsub';
import { useRootRef } from '../../hooks/useRootRef';
import { Icon } from '../jdsicon/generated';
import { NudgeProps } from '../jdsnudge/logic';

const DEFAULT_DISMISS_WITH_CTA = 10;
const DEFAULT_DISMISS_WITHOUT_CTA = 5;

export const resolvePrefixIcon = (props: ToastProps | NudgeProps) => {
  if (typeof props.prefix === 'string') return <Icon ic={props.prefix} />;

  return props.prefix;
};

function getDismissTime(isCTAPresent: boolean, dismissAfter?: number): number {
  let timer = DEFAULT_DISMISS_WITHOUT_CTA;
  if (isCTAPresent) {
    timer = DEFAULT_DISMISS_WITH_CTA;
  }

  if (dismissAfter !== undefined) {
    const defaultMinTimer = 2;
    const defaultMaxTimer = isCTAPresent
      ? DEFAULT_DISMISS_WITH_CTA
      : DEFAULT_DISMISS_WITHOUT_CTA;

    if (dismissAfter < defaultMinTimer) {
      timer = defaultMinTimer;
    } else if (dismissAfter < defaultMaxTimer) {
      timer = dismissAfter;
    }
  }

  return timer;
}

export const withLogic =
  (Component: React.ComponentType<ToastProps>): React.FC<ToastProps> =>
  (userProps: ToastProps) => {
    const props = modifyProps(userProps, PROP_MAP);
    const {
      onClose,
      onShow,
      primaryCTA,
      secondaryCTA,
      showClose,
      id,
      suffix,
      dismissAfter,
      ...rest
    } = props;

    const [closeToast, setCloseToast] = useState<boolean>();
    const [timeToClose, setTimeToClose] = useState(0);
    const [intervalID, setIntervalID] = useState<NodeJS.Timer>();

    const { rootRef, element } = useRootRef();

    const isCTAPresent = !!(primaryCTA || secondaryCTA);

    const TOAST_CLOSING_TIME = getDismissTime(isCTAPresent, dismissAfter);

    const handleToastUnmount = useCallback(() => {
      if (id) {
        toast.dismiss(id);
        onClose?.(id);

        if (element?.classList.contains('visible')) {
          element.classList.remove('visible');
        }

        setCloseToast(true);
      }
    }, [id, onClose]);

    const handleToastStart = useCallback(() => {
      if (timeToClose !== TOAST_CLOSING_TIME) {
        const intervalID = setInterval(() => {
          setTimeToClose((time) => {
            if (time === 10) {
              clearInterval(intervalID);
            }
            return time + 0.5;
          });
        }, 500);
        setIntervalID(intervalID);
      }
    }, [TOAST_CLOSING_TIME, timeToClose]);

    const handleToastPause = useCallback(() => {
      if (timeToClose !== TOAST_CLOSING_TIME) clearInterval(intervalID);
    }, [timeToClose, TOAST_CLOSING_TIME, intervalID]);

    useEffect(() => {
      id && onShow && onShow(id);
      setTimeout(() => setCloseToast(false), 10);
      const { unsubscribe: toastCloseUnsubscribe } = subscribe(
        NotificationCloseService,
        (data) => {
          if (id && data[id] === 'toast') {
            handleToastClose();
          }
        }
      );
      if (window) {
        handleToastStart();
        window.addEventListener('blur', handleToastPause);

        window.addEventListener('focus', handleToastStart);
      }

      return () => {
        if (window) {
          window.removeEventListener('blur', handleToastPause);

          window.removeEventListener('focus', handleToastStart);
        }
        id && onClose && onClose(id);
        toastCloseUnsubscribe();
      };
    }, []);

    useEffect(() => {
      if (timeToClose === TOAST_CLOSING_TIME) handleToastUnmount();
    }, [timeToClose, TOAST_CLOSING_TIME]);

    const handleToastClose = () => {
      setCloseToast(true);
    };
    return (
      <Component
        {...{
          onClose: handleToastUnmount,
          onMouseEnter: () => {
            handleToastPause();
          },
          onMouseLeave: () => {
            handleToastStart();
          },
          ...rest,
          prefix: resolvePrefixIcon(props),
          open: true,
          suffix: suffix?.props ? suffix : <NotificationButton {...suffix} />,
          className: closeToast === false ? 'visible' : '',
          rootRef,
          _hasCTA: Boolean(primaryCTA || secondaryCTA),
          _hasDescription: Boolean(rest.description),
          _platform: getPlatform(),
          showClose,
          primaryCTA,
          secondaryCTA
        }}
      />
    );
  };
export { ToastProps };
